import React, { FC, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';
import styled from '@emotion/styled';
import get from 'lodash/get';

import {
    BodyTextMedium,
    BodyTextSmall,
    CalloutInline,
    ButtonGroup,
    Button,
    HeadingText50,
    Link,
    pxToRem,
} from '@modernhealth/vital-components';

import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { colors } from '@modernhealth/vital-foundation';
import * as Sentry from '@sentry/browser';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { type LocationDescriptor, type LocationState } from 'history';

import { hexToRgba } from 'src/shared/helpers/hexToRgba';
import { costFormattedUSD } from 'src/shared/helpers/formatNumberToCurrency';
import { useCancelCoachingSessionMutation } from 'src/modules/videoSessions/documents/cancelCoachingSessionMutation.generated';
import { useConfirmVisitWithoutPaymentMutation } from 'src/modules/payments/documents/ConfirmVisitWithoutPayment.generated';
import { useCreateIframeLinkMutation } from 'src/modules/payments/documents/CreateIframeLink.generated';
import { useGetCancelledSessionReviewQuery } from 'src/modules/videoSessions/documents/getCancelledSessionReview.generated';
import { Dispatch } from 'src/config/rootStore';
import { analyticsClient } from 'src/shared/services/analytics';
import { ScheduleSessionConfirmationModal } from 'src/modules/videoSessions/components/ScheduleSessionConfirmationModal';
import {
    Calendar,
    getCalendarsForSession,
    openCalendarForSession,
} from 'src/modules/videoSessions/components/AddToCalendarModal';
import {
    getVideoSession,
    getVideoSessions,
    getSessionUtilizationInfo,
} from 'src/modules/videoSessions/selectors/videoSessionsSelectors';
import {
    CoachingVisitNode,
    CoachingVisitState,
    CoachNode,
    CostEstimation,
} from 'src/generated/webapp_gql';
import { fetchIframeUrl } from 'src/modules/payments/helpers/paymentHelpers';
import { SessionReviewProviderAvatar } from 'src/modules/payments/components/SessionReviewProviderAvatar';
import { SessionReviewItemsList } from 'src/modules/payments/components/SessionReviewItemsList';
import { toastNotificationsService } from 'src/config/rootService';
import { config } from 'src/config/config';
import { CostModal } from 'src/modules/payments/components/CostModal';
import { LoadingModal } from 'src/modules/payments/components/LoadingModal';
import { useFormattedSessionDateTimeRange } from 'src/modules/videoSessions/DateTimeFormatter';
import { CancelSessionConfirmationModal } from 'src/modules/payments/components/CancelSessionConfirmationModal';
import { SessionSurveyModal } from 'src/modules/videoSessions/components/SessionSurveyModal';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';

import { PaymentsIframe } from './PaymentsIframe';
import { ContactSupportModal } from './ContactSupportModal';
import { AutopayCallout } from './AutopayCallout';
import { ProviderType } from 'src/modules/user/models/user';
import { color } from 'src/shared/styles/variables';
import { InlineButtonLink } from 'src/modules/messaging/components/InlineButtonLink';
import { ExternalLink } from 'src/shared/components/ExternalLink';
import { getMyProviderAssignments } from 'src/modules/providers/selectors/providersSelectors';
import { usePathwayByAssignmentQuery } from 'src/modules/pathways/documents/pathwayByAssignment.generated';
import { captureException } from 'src/shared/helpers/captureException';

type Props = {
    chargeAmount?: number;
    costEstimation?: CostEstimation;
    dataTestid?: string;
    isLoadingPaymentInfo?: boolean;
    onboardingMode?: boolean;
    provider: Pick<
        CoachNode,
        'uuid' | 'providerType' | 'firstName' | 'lastName' | 'lateCancellationThreshold'
    >;
    sessionUuid: string;
    shouldDisableSessionReviewItemsList?: boolean;
    start: moment.Moment;
    end: moment.Moment;
    isSuccess?: boolean;
    state: CoachingVisitState;
};

export const SessionReview: FC<Props> = ({
    chargeAmount,
    costEstimation,
    dataTestid,
    isLoadingPaymentInfo = false,
    onboardingMode = false,
    provider,
    sessionUuid,
    start,
    end,
    isSuccess,
    state,
}) => {
    const history = useHistory();
    const { t } = useTranslation();
    const dispatch = useDispatch<Dispatch>();
    const user = useSelector(getUserInformation);
    const { hpiAutomaticPaymentsEnabled } = useFlags();

    const [isContactUsModalVisible, setIsContactUsModalVisible] = useState<boolean>(false);
    const [costModalVisible, setCostModalVisible] = useState<boolean>(false);
    const [isPammInitialEvaluationBooked, setIsPammInitialEvaluationBooked] =
        useState<boolean>(false);
    const [isBookingConfirmationVisible, setIsBookingConfirmationVisible] = useState<boolean>(
        !!isSuccess,
    );
    const [sessionSurveyModalVisible, setSessionSurveyModalVisible] = useState<boolean>(false);
    const [isIframeLoading, setIframeElementLoading] = useState<boolean>(false);
    const [isConfirmingSessionCancellation, setIsConfirmingSessionCancellation] =
        useState<boolean>(false);
    const [navigationUrl, setNavigationUrl] = useState<string>('');
    const [navigationState, setNavigationState] = useState<LocationState>();

    const session = useSelector(getVideoSession(sessionUuid));
    const sessions = useSelector(getVideoSessions);
    const sessionUtilizationInfo = useSelector(getSessionUtilizationInfo);
    const coachAssignmentUuid =
        useSelector(getMyProviderAssignments)?.find(({ coach }) => coach?.uuid === provider.uuid)
            ?.uuid || '';
    const isHDHP = costEstimation?.planType === 'HDHP';
    const isPamm = provider.providerType === ProviderType.PSYCHIATRIST;
    const isExtendedTherapySession =
        provider.providerType === ProviderType.THERAPIST &&
        sessionUtilizationInfo.therapyVisitsRemaining <= 0 &&
        user.hpiEligibilityStatus;
    const hasUpcomingSessions = sessions?.some(
        (s: CoachingVisitNode) =>
            s.state === CoachingVisitState.Scheduled && s.visitType === 'Health Plan',
    );

    const [cancelSession, { loading: isCancelingSession }] = useCancelCoachingSessionMutation({
        variables: { sessionUuid },
    });
    const [createIframeLink] = useCreateIframeLinkMutation({
        variables: {
            visitUuid: sessionUuid,
        },
    });

    const [confirmSessionWithoutPayment, { loading: isConfirmingSession }] =
        useConfirmVisitWithoutPaymentMutation({
            variables: { coachingVisitUuid: sessionUuid },
        });
    const result = useGetCancelledSessionReviewQuery({
        variables: {},
    });

    const { data: cancelledSessionReviews, loading: isGettingCancelledSessionReviews } = result;

    const sessionSlot = { start, end };
    const chargeAmountText = costFormattedUSD(chargeAmount);
    const isLoading =
        isCancelingSession ||
        isLoadingPaymentInfo ||
        isConfirmingSession ||
        isGettingCancelledSessionReviews;
    const isUserEnrolledInAutopay = user.isEnrolledInAutopayments && hpiAutomaticPaymentsEnabled;
    const isNoCopaySession =
        state === CoachingVisitState.PendingConfirmation && (!chargeAmount || chargeAmount <= 0);
    const { data: pathwayAssignmentData } = usePathwayByAssignmentQuery({
        variables: {
            providerAssignmentUuid: coachAssignmentUuid,
        },
        skip: !coachAssignmentUuid,
    });
    const creationSourceToUrl = {
        PATHWAYS: `/pathways/${pathwayAssignmentData?.pathwayByAssignment?.uuid}`,
    };
    let secondButtonText =
        isUserEnrolledInAutopay || isNoCopaySession
            ? t('payments:book_session')
            : t('payments:proceed_pay');
    const wasBookedByProvider = session?.createdByUuid === provider.uuid;
    let sessionReviewDescription = isNoCopaySession
        ? t('payments:session_review.description_no_copay')
        : t('payments:session_review.description', {
              providerName: `${provider?.firstName} ${provider?.lastName}`,
          });

    const attemptNavigation = (nextLocation: LocationDescriptor, nextState?: LocationState) => {
        if (
            (nextLocation && sessionSurveyModalVisible) ||
            cancelledSessionReviews?.getCancelledSessionReview?.ok ||
            nextLocation !== '/schedule-session/calendar' ||
            !isExtendedTherapySession
        ) {
            dispatch.videoSessions.fetchSessions(); // Re-fetch sessions after one was removed
            dispatch.user.fetchUserInfo(); // Re-fetch user info since we're getting session utilization info from `me` query
            history.push(nextLocation, nextState);
        } else {
            setSessionSurveyModalVisible(true);
            setNavigationUrl(nextLocation);
            setNavigationState(nextState);
        }
    };

    const handleBackToSchedule = () => {
        analyticsClient.trackClick('Back to schedule', {
            page_name: 'Payment Session Review Page',
        });
        handleSessionCancellation();
        dispatch.videoSessions.fetchSessions(); // Re-fetch sessions after one was removed
        dispatch.user.fetchUserInfo(); // Re-fetch user info since we're getting session utilization info from `me` query
        attemptNavigation('/schedule-session/calendar', {
            coachUuid: provider.uuid,
            disableBack: true,
            isFirstSession: false,
            onboardingMode,
        });
    };

    const handleSessionCancelConfirmation = () => {
        analyticsClient.trackClick('Cancel session', { page_name: 'Payment Session Review Page' });
        handleSessionCancellation();
        toastNotificationsService.renderSuccessToast(
            t('translation:notifications.sessionCanceled'),
        );
        attemptNavigation('/dashboard', { disableBack: true });
    };

    const handleSessionCancellation = async () => {
        try {
            const { data } = await cancelSession();

            if (!data?.cancelEvent?.ok) {
                throw new Error();
            }
        } catch (err) {
            toastNotificationsService.renderErrorToast(t(config.messages.genericError));
            Sentry.captureException(err);
            captureException(err);
        }
    };

    const handleCostClick = () => {
        analyticsClient.trackClick('Estimated cost', { page_name: 'Payment Session Review Page' });
        setCostModalVisible(true);
    };

    const handleCostModalClose = () => {
        setCostModalVisible(false);
    };

    const handleProceedPay = () => {
        analyticsClient.trackClick('Proceed to pay', { page_name: 'Payment Session Review Page' });

        handleCostModalClose();

        proceedToIframe();
    };

    const handleScheduleSession = () => {
        if (isPamm) {
            attemptNavigation('/dashboard');
            return;
        }
        attemptNavigation('/schedule-session/calendar', {
            coachUuid: provider.uuid,
            disableBack: true,
            isFirstSession: false,
            onboardingMode,
        });
    };

    const handleAddToCalendar = (calendar: Calendar) => {
        if (!session) {
            return;
        }

        openCalendarForSession(session, calendar);
    };

    const proceedToIframe = async () => {
        try {
            setIframeElementLoading(true);
            const { data } = await createIframeLink();
            if (!data?.createSAMLPostPage?.ok) {
                throw new Error();
            }

            const samlPage = data?.createSAMLPostPage?.samlPage ?? '';
            const targetUrl = process.env.REACT_APP_INSTAMED_IFRAME_INCONTEXT_URL;

            fetchIframeUrl(samlPage, targetUrl);
        } catch (err) {
            setIframeElementLoading(false);
            toastNotificationsService.renderErrorToast(config.messages.genericError);
            Sentry.captureException(err);
            captureException(err);
        }
    };

    const handleBookSession = async () => {
        try {
            const { data } = await confirmSessionWithoutPayment();

            if (!data?.confirmVisitWithoutPayment?.ok) {
                throw new Error();
            }

            setIsBookingConfirmationVisible(true);
        } catch (err) {
            const message = get(err, 'message');
            const errorMessage = message.replace('GraphQL error: ', '');
            if (
                errorMessage ===
                `['Multiple initial psychiatry evaluation visits booked for member ${user.uuid}.']`
            )
                setIsPammInitialEvaluationBooked(true);
            else {
                setIsBookingConfirmationVisible(false);
                toastNotificationsService.renderErrorToast(config.messages.genericError);
                Sentry.captureException(err);
                captureException(err);
            }
        }
    };

    const goHome = () => attemptNavigation('/dashboard');
    const goToNextScreen = () =>
        attemptNavigation(
            !session?.creationSource || !creationSourceToUrl[session?.creationSource]
                ? '/dashboard'
                : creationSourceToUrl[session?.creationSource],
        );

    const secondButtonOnClick =
        isUserEnrolledInAutopay || isNoCopaySession || isPamm
            ? handleBookSession
            : handleProceedPay;
    let backToButtonText = wasBookedByProvider
        ? t('insuranceForm:insurance_approved.back_home')
        : t('payments:back_schedule');

    if (isPamm) {
        sessionReviewDescription = t('videoSessions:session_review.description', {
            providerName: `${provider?.firstName} ${provider?.lastName}`,
        });
        backToButtonText = t('common.back');
        secondButtonText = t('videoSessions:session_review.confirm');
    }

    const backToButtonClick = () => {
        if (navigationUrl) {
            attemptNavigation(navigationUrl, navigationState);
        } else if (wasBookedByProvider) {
            goHome();
        } else {
            handleBackToSchedule();
        }
    };

    useEffect(() => {
        const fetchLatestAutopayEnrollmentStatus = async () => {
            await dispatch.user.fetchUserInfo();
        };

        fetchLatestAutopayEnrollmentStatus();
    }, [dispatch.user]);

    useEffect(() => {
        return () => {
            // fix memory leak, if app is interrupted during iframe loading
            setIframeElementLoading(false);
        };
    }, []);

    const sessionDate = useFormattedSessionDateTimeRange({
        start: start.toDate(),
        end: end.toDate(),
    });

    const handleHref = e => {
        e.preventDefault();
        handleCostClick();
    };

    return (
        <PageContainer data-testid={dataTestid}>
            <SessionReviewProviderAvatar provider={provider} />
            <HeadingText50 marginTop={24}>{t('payments:session_review.title')}</HeadingText50>
            <BodyTextMedium marginBottom={24} fontColor="gray65">
                {sessionReviewDescription}
            </BodyTextMedium>
            {isHDHP && hasUpcomingSessions && !isPamm && (
                <CalloutContainer>
                    <CalloutInline variant="default">
                        {t('payments:multiple_session_warning')}
                    </CalloutInline>
                </CalloutContainer>
            )}
            {isUserEnrolledInAutopay && <AutopayCallout />}
            <SessionReviewItemsList
                onCostClick={handleCostClick}
                sessionCost={chargeAmountText}
                sessionDate={sessionDate}
                isPamm={isPamm}
                sessionInfo={session}
            />
            {isPamm && (
                <CalloutBottom>
                    <CalloutInline variant="default">
                        <BodyTextSmall>
                            {t('payments:session_review.review_items.cost_description')}{' '}
                            <Link css={{ whiteSpace: 'nowrap', color: color.royalBlue }}>
                                <span role="button" onClick={handleHref}>
                                    {t('payments:session_review.review_items.cost_description_cta')}
                                </span>
                            </Link>
                        </BodyTextSmall>
                        <BodyTextSmall marginTop={16}>
                            {t('videoSessions:session_review.description.pamm.part2')}
                        </BodyTextSmall>
                    </CalloutInline>
                </CalloutBottom>
            )}
            {isPammInitialEvaluationBooked && (
                <CalloutBottom>
                    <CalloutInline variant="danger">
                        <BodyTextSmall>
                            {t('payments:pamm_initial_evaluation_session.already_booked')}
                        </BodyTextSmall>
                    </CalloutInline>
                </CalloutBottom>
            )}

            <ButtonGroup css={{ marginTop: pxToRem(24) }}>
                <Button onClick={backToButtonClick} busy={isCancelingSession}>
                    {backToButtonText}
                </Button>
                <Button
                    onClick={secondButtonOnClick}
                    busy={isLoadingPaymentInfo}
                    disabled={isLoading}
                >
                    {secondButtonText}
                </Button>
            </ButtonGroup>
            {wasBookedByProvider && (
                <StyledButton
                    busy={isCancelingSession}
                    onClick={() => setIsConfirmingSessionCancellation(true)}
                    size="large"
                    variant="destructive"
                >
                    {t('videoSessions:session_details.cancel')}
                </StyledButton>
            )}
            <ResponsibilityBox marginTop={24}>
                <Link>
                    <Trans
                        t={t}
                        ns="payments"
                        i18nKey="session_review.responsibility"
                        components={{
                            url: (
                                <ExternalLink
                                    href={`${config.modernHealthURL}/consent-to-telehealth-treatment`}
                                    openInNewTab
                                />
                            ),
                        }}
                    />
                </Link>
            </ResponsibilityBox>
            <ContactUsText marginTop={24}>
                <Link>
                    <Trans
                        t={t}
                        ns="payments"
                        i18nKey="session_review.contact_us"
                        components={{
                            url: (
                                <InlineButtonLink
                                    onClick={() => setIsContactUsModalVisible(true)}
                                    key="session_review.contact_us"
                                />
                            ),
                        }}
                    />
                </Link>
            </ContactUsText>
            {costModalVisible && (
                <CostModal
                    visible={costModalVisible}
                    onConfirm={handleProceedPay}
                    onClose={handleCostModalClose}
                    costEstimation={costEstimation}
                    type={costEstimation?.planType}
                    isPamm={isPamm}
                />
            )}
            <ScheduleSessionConfirmationModal
                visible={isBookingConfirmationVisible}
                onExit={goToNextScreen}
                onSkip={goHome}
                onSessionBookedConfirm={handleScheduleSession}
                onAddToCalendar={handleAddToCalendar}
                calendars={getCalendarsForSession(session)}
                onboardingMode={onboardingMode}
                loading={isLoading}
                sessionSlot={sessionSlot}
                sessionBooked
                provider={provider}
                closable={false}
                session={session}
            />
            <LoadingModal isVisible={isIframeLoading} />
            <PaymentsIframe
                sessionUuid={sessionUuid}
                closeLoadingModal={() => setIframeElementLoading(false)}
            />
            {isConfirmingSessionCancellation && (
                <CancelSessionConfirmationModal
                    isLoading={isCancelingSession}
                    isVisible={isConfirmingSessionCancellation}
                    onBack={() => setIsConfirmingSessionCancellation(false)}
                    onConfirm={handleSessionCancelConfirmation}
                />
            )}
            <ContactSupportModal
                isVisible={isContactUsModalVisible}
                onModalClose={() => setIsContactUsModalVisible(false)}
            />
            <SessionSurveyModal
                onClose={backToButtonClick}
                visible={sessionSurveyModalVisible}
                coachingVisitUuid={sessionUuid}
            />
        </PageContainer>
    );
};

const StyledButton = styled(Button)({
    marginTop: pxToRem(16),
});

const PageContainer = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    width: pxToRem(524),
    padding: '1rem',
    margin: '1rem auto 3rem',
});

const ResponsibilityBox = styled(BodyTextSmall)({
    width: '100%',
    backgroundColor: hexToRgba(colors.gray05, 0.5),
    color: colors.gray85,
    borderRadius: 4,
    padding: `${pxToRem(10)} ${pxToRem(12)}`,
});

const ContactUsText = styled(BodyTextSmall)({
    width: '100%',
    textAlign: 'center',
});

const CalloutContainer = styled.div({
    marginBottom: '20px',
});

const CalloutBottom = styled.div({
    marginTop: '20px',
});
